import {
  ContentAudience,
  ContentPriority,
  ContentQualityQuestionData,
  ContentQualityResponseOptions,
  GetContentQualityEvaluationResponse,
  GetContentQualityQuestionsResponse,
  QuestionResponse,
  QuestionnaireResponses,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { IMplServiceClient } from '../api/IMplServiceClient';
import { promiseTimeout } from '../api/UtilsApi';
import { getCurrentContentState } from '../api/WorkflowProgressUtil';
import { log } from '../loggers/Logger';
import { ContentQualityQuestionnaireState } from '../common/ContentQualityQuestionnaireState';
import LocalStorage from '../common/LocalStorage';
import {
  CategoryOrder,
  calculateQualityScore,
  createQuestionnaireResponses,
  createSortedCategories,
  isScoreValid,
  populateDependentMap,
  populateQuestionMaps,
  responsesArrayToMap,
  responsesMapToArray,
  updateDependentResponses,
  updateVisibilityMap,
  COMMENT_MAX,
} from '../components/utils/ContentQualityUtils';
import { ContentEditorService } from './ContentEditorService';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** Whether the bar-raiser questionnaire has finished loading. */
export type BarRaiserEvaluationLoadState = 'idle' | 'loading' | 'ready' | 'error';

/** Which submit action, if any, is currently in flight or has completed. */
export type BarRaiserEvaluationSubmitState =
  | 'idle'
  | 'publishing'
  | 'rejecting'
  | 'publish_succeeded'
  | 'reject_succeeded'
  | 'publish_failed'
  | 'reject_failed';

/** How often the submit flow refreshes the workflow while waiting for the target state. */
const POLL_INTERVAL_MS = 5000;

/** How long the submit flow waits for the target state before reporting failure. */
const SUBMIT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Cuts a stored comment to the character limit, treating a missing comment as empty. */
function truncateComment(comment: string | undefined): string {
  const cut = (comment ?? '').slice(0, COMMENT_MAX);

  // A cut through a two-unit character (emoji, rare Chinese) keeps only its first half, so drop that half rather than show a broken symbol.
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
}

export type BarRaiserEvaluationEvents = {
  /** Fires whenever any observable state changes. */
  changed: () => void;
};

/**
 * Owns the bar-raiser quality-evaluation questionnaire for the open content:
 * loads the question set and the last evaluation, tracks responses and the
 * live quality score, and submits the publish/reject decision.
 *
 * All async operations are serialized via PQueue. Response edits are
 * synchronous in-memory mutations that recompute the score and emit `changed`.
 */
export class BarRaiserEvaluationService {
  private readonly _emitter = new TypedEmitter<BarRaiserEvaluationEvents>();
  private readonly _mpl: IMplServiceClient;
  private readonly _contentEditorService: ContentEditorService;
  private readonly _queue = new PQueue({ concurrency: 1 });

  private _loadState: BarRaiserEvaluationLoadState = 'idle';
  private _submitState: BarRaiserEvaluationSubmitState = 'idle';
  private _errorMessage: string | undefined = undefined;
  /** Content published in this session whose confirmation notice has not yet been dismissed. */
  private _lastPublishedContentId: string | undefined = undefined;
  /** Bumped on every load and on close; async work discards itself once its captured value goes stale. */
  private _generation = 0;
  private _contentId: string | undefined = undefined;
  private _workflowId: number | undefined = undefined;
  private _requiresReleasePriority = false;

  private _questions: ContentQualityQuestionData[] = [];
  private _idToSequence = new Map<string, number>();
  private _sequenceToQuestion = new Map<number, ContentQualityQuestionData>();
  private _categoryToSequences = new Map<string, number[]>();
  private _categories: CategoryOrder[] = [];
  private _dependentMap = new Map<number, number[]>();
  private _visibilityMap = new Map<number, boolean>();
  private _responseMap = new Map<number, QuestionResponse>();
  private _score = -1;

  constructor(mpl: IMplServiceClient, contentEditorService: ContentEditorService) {
    this._mpl = mpl;
    this._contentEditorService = contentEditorService;
  }

  get loadState(): BarRaiserEvaluationLoadState {
    return this._loadState;
  }

  get submitState(): BarRaiserEvaluationSubmitState {
    return this._submitState;
  }

  get errorMessage(): string | undefined {
    return this._errorMessage;
  }

  /** Content just published in this session, until its confirmation notice is dismissed; `undefined` otherwise. */
  get lastPublishedContentId(): string | undefined {
    return this._lastPublishedContentId;
  }

  /** True when the open content is external-audience and needs a release priority to publish. */
  get requiresReleasePriority(): boolean {
    return this._requiresReleasePriority;
  }

  /** The live quality score, or -1 when the questionnaire is incomplete. */
  get score(): number {
    return this._score;
  }

  get categories(): readonly CategoryOrder[] {
    return this._categories;
  }

  get questionsBySequence(): ReadonlyMap<number, ContentQualityQuestionData> {
    return this._sequenceToQuestion;
  }

  get categoryToSequences(): ReadonlyMap<string, number[]> {
    return this._categoryToSequences;
  }

  get visibility(): ReadonlyMap<number, boolean> {
    return this._visibilityMap;
  }

  get responses(): ReadonlyMap<number, QuestionResponse> {
    return this._responseMap;
  }

  /**
   * Load the question set and last evaluation for `contentId`, resetting any
   * previous submit outcome. Call each time the panel mounts. Serialized.
   * In-progress answers survive because they are restored from the local draft.
   * A submit still in flight stops polling and drops its outcome in favor of this load.
   */
  load(contentId: string): void {
    // Bumped now, not when the load dequeues, so a submit still polling sees it and stops.
    const generation = ++this._generation;
    void this._queue.add(() => this.doLoad(contentId, generation));
  }

  /**
   * Update one question's response. Recomputes dependents, visibility, and the
   * live score, then emits. Synchronous (no I/O) so the gauge tracks each edit.
   */
  setResponse(sequence: number, response: ContentQualityResponseOptions, comment: string): void {
    // Apply the new response.
    const updatedResponseMap = new Map(this._responseMap);
    updatedResponseMap.set(sequence, { response, comment });

    // When a parent answer flips to N/A, drop its dependents and refresh visibility.
    const updatedVisibilityMap = new Map(this._visibilityMap);
    const visibilityShouldUpdate = updateDependentResponses(
      sequence,
      response,
      this._dependentMap,
      updatedResponseMap,
    );
    if (visibilityShouldUpdate) {
      updateVisibilityMap(
        this._idToSequence,
        this._sequenceToQuestion,
        updatedResponseMap,
        updatedVisibilityMap,
      );
    }

    this._responseMap = updatedResponseMap;
    this._visibilityMap = updatedVisibilityMap;
    this._score = calculateQualityScore(this._responseMap, this._visibilityMap);
    this.saveDraft();
    this._emitter.emit('changed');
  }

  /** Clear the current error message, if any. Synchronous. */
  clearError(): void {
    if (this._errorMessage === undefined) return;
    this._errorMessage = undefined;
    this._emitter.emit('changed');
  }

  /** Dismiss the just-published confirmation notice so it stops showing. Synchronous. */
  dismissLastPublished(): void {
    if (this._lastPublishedContentId === undefined) return;
    this._lastPublishedContentId = undefined;
    this._emitter.emit('changed');
  }

  /**
   * Persist the evaluation and approve the content. Serialized.
   * For external-audience content, pass the release priority assigned at approve time.
   * @throws never — failures are surfaced via {@link errorMessage}.
   */
  async publish(releasePriority?: ContentPriority): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() =>
      this.doSubmit(
        generation,
        'publishing',
        WorkflowEventTypes.PUBLISHER_REVIEW_APPROVE,
        true,
        releasePriority,
      ),
    );
  }

  /**
   * Persist the evaluation and reject the content. Serialized.
   * @throws never — failures are surfaced via {@link errorMessage}.
   */
  async reject(): Promise<void> {
    const generation = this._generation;
    await this._queue.add(() =>
      this.doSubmit(generation, 'rejecting', WorkflowEventTypes.PUBLISHER_REVIEW_REJECT, false),
    );
  }

  on<K extends keyof BarRaiserEvaluationEvents>(
    event: K,
    listener: BarRaiserEvaluationEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  /** Release all resources. Call on provider unmount. */
  close(): void {
    this._generation++;
    this._queue.clear();
    this._emitter.removeAllListeners();
  }

  private async doLoad(contentId: string, generation: number): Promise<void> {
    // A newer load or a close has superseded this one while it waited in the queue.
    if (generation !== this._generation) return;

    // Reset to a clean loading state for this content.
    this._loadState = 'loading';
    this._errorMessage = undefined;
    this._submitState = 'idle';
    this._contentId = contentId;
    this._emitter.emit('changed');

    // Resolve the content type and workflow from the already-open document.
    const content = this._contentEditorService.content;
    const handle = this._contentEditorService.loadedDocumentHandle;
    const contentType = content?.contentMetadata?.contentType;
    const workflowId =
      handle?.document.kind === 'draft' ? handle.document.workflow.draftWorkflowId : undefined;
    if (!contentType || workflowId === undefined) {
      this.setLoadError('Content type or draft workflow is unavailable for the open document.');
      return;
    }
    this._workflowId = workflowId;

    // External-audience content needs a release priority assigned at approve time.
    const audience =
      handle?.document.kind === 'draft' ? handle.document.workflow.contentAudience : undefined;
    this._requiresReleasePriority = audience !== ContentAudience.INTERNAL;

    // Fetch the question set and the last submitted evaluation together; only
    // the network calls are expected to throw, so only they are guarded.
    let questionsResponse: GetContentQualityQuestionsResponse;
    let lastEvaluation: GetContentQualityEvaluationResponse;
    try {
      [questionsResponse, lastEvaluation] = await Promise.all([
        this._mpl.getQuestionnaireQuestions(contentType),
        this._mpl.getLastContentQualityEvaluation(contentId),
      ]);
    } catch (err) {
      // Log before the stale check so a superseded failure still reaches monitoring.
      log.error({ error: err, contentId }, 'Failed to load bar raiser evaluation');
      if (generation !== this._generation) return;
      this.setLoadError('Failed to load the evaluation. Please reload and try again.');
      return;
    }

    // A newer load or a close arrived during the fetch, so these results are stale.
    if (generation !== this._generation) return;

    // Prefer an unsubmitted local draft (questions + responses) over the
    // server question set, so a bar raiser can resume after a reload.
    const draft = LocalStorage.getContentQualityQuestionnaireState(contentId, workflowId);
    const questions = draft && draft.questions ? draft.questions : questionsResponse.questions;

    // Build the static question/category maps.
    const idToSequence = new Map<string, number>();
    const sequenceToQuestion = new Map<number, ContentQualityQuestionData>();
    const categoryToSequences = new Map<string, number[]>();
    const categories: CategoryOrder[] = [];
    populateQuestionMaps(questions, idToSequence, sequenceToQuestion, categoryToSequences);
    createSortedCategories(categoryToSequences, categories);

    // Seed responses from the local draft when present, else the last evaluation.
    const seededResponses = draft
      ? draft.responses
      : lastEvaluation.responses.map((r) => ({
          sequence: Number(r.questionSequence),
          response: { response: r.response, comment: r.comment },
        }));

    // Older evaluations may hold comments over the limit, so cut them before display.
    const responseMap = responsesArrayToMap(
      seededResponses.map(({ sequence, response }) => ({
        sequence,
        response: { ...response, comment: truncateComment(response.comment) },
      })),
    );

    // Derive dependents, visibility, and the initial score.
    const dependentMap = new Map<number, number[]>();
    populateDependentMap(idToSequence, sequenceToQuestion, dependentMap);
    const visibilityMap = new Map<number, boolean>();
    updateVisibilityMap(idToSequence, sequenceToQuestion, responseMap, visibilityMap);

    this._questions = questions;
    this._idToSequence = idToSequence;
    this._sequenceToQuestion = sequenceToQuestion;
    this._categoryToSequences = categoryToSequences;
    this._categories = categories;
    this._dependentMap = dependentMap;
    this._visibilityMap = visibilityMap;
    this._responseMap = responseMap;
    this._score = calculateQualityScore(responseMap, visibilityMap);
    this._loadState = 'ready';
    this._emitter.emit('changed');
  }

  /** Move into the error load state and notify subscribers. */
  private setLoadError(message: string): void {
    this._loadState = 'error';
    this._errorMessage = message;
    this._emitter.emit('changed');
  }

  private async doSubmit(
    generation: number,
    submitState: Extract<BarRaiserEvaluationSubmitState, 'publishing' | 'rejecting'>,
    event: WorkflowEventTypes,
    requireValidScore: boolean,
    releasePriority?: ContentPriority,
  ): Promise<void> {
    // A load or a close since the click means the user no longer sees the questionnaire they submitted.
    if (generation !== this._generation) return;

    // Guard against submitting before load completes.
    if (this._contentId === undefined || this._workflowId === undefined) {
      this._errorMessage = 'The evaluation is not ready to submit yet.';
      this._emitter.emit('changed');
      return;
    }

    // Validate before any network call.
    const validationError = this.validateForSubmit(requireValidScore, releasePriority);
    if (validationError) {
      this._errorMessage = validationError;
      this._emitter.emit('changed');
      return;
    }

    const isPublish = submitState === 'publishing';
    this._submitState = submitState;
    this._errorMessage = undefined;
    this._emitter.emit('changed');

    const evaluation: QuestionnaireResponses = {
      responses: createQuestionnaireResponses(this._idToSequence, this._responseMap),
    };

    try {
      // Post the workflow decision carrying the evaluation + score (the service
      // persists the questionnaire from the event), then refresh local workflow state.
      await this._mpl.postWorkflowEvent(
        this._contentId,
        this._workflowId,
        event,
        '',
        undefined,
        evaluation,
        this._score,
        { suppressSuccessFlashbar: true },
      );
      // For external-audience approvals, persist the bar-raiser release priority.
      if (releasePriority !== undefined) {
        await this._mpl.updateContentPriority(this._contentId, releasePriority);
      }
      // The evaluation is persisted server-side now; drop the local draft.
      this.clearDraft();

      // The state change is applied asynchronously server-side: poll the workflow
      // until it reports the target state, then surface the outcome.
      const targetState = isPublish ? WorkflowState.PUBLISHED : WorkflowState.REJECTED;
      const reached = await this.pollUntilState(targetState, generation);

      // A load or a close during the submit owns the state now, so this outcome is stale.
      if (generation !== this._generation) return;

      this._submitState = isPublish
        ? reached
          ? 'publish_succeeded'
          : 'publish_failed'
        : reached
          ? 'reject_succeeded'
          : 'reject_failed';

      // On a confirmed publish, mark the content so its view can confirm the publish once.
      if (isPublish && reached) this._lastPublishedContentId = this._contentId;
    } catch (err) {
      log.error(
        { error: err, contentId: this._contentId },
        'Failed to submit bar raiser evaluation',
      );
      if (generation !== this._generation) return;
      this._errorMessage = 'Failed to submit the evaluation. Please try again.';
      this._submitState = isPublish ? 'publish_failed' : 'reject_failed';
    }
    this._emitter.emit('changed');
  }

  /**
   * Refresh the workflow until it reaches `targetState`, returning true once it does.
   * Returns false on timeout (5 minutes) or once `generation` goes stale.
   */
  private async pollUntilState(targetState: WorkflowState, generation: number): Promise<boolean> {
    const deadline = Date.now() + SUBMIT_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      if (this._generation !== generation) return false;

      await this._contentEditorService.refreshWorkflow();
      if (this._generation !== generation) return false;
      if (this.isCurrentlyInState(targetState)) return true;

      await promiseTimeout(POLL_INTERVAL_MS);
    }
    return false;
  }

  /** Whether the open document's workflow is currently in `targetState`. */
  private isCurrentlyInState(targetState: WorkflowState): boolean {
    const doc = this._contentEditorService.loadedDocumentHandle?.document;
    if (doc === undefined || (doc.kind !== 'draft' && doc.kind !== 'finalizing')) return false;
    return getCurrentContentState(doc.workflow) === targetState;
  }

  /** Persist the in-progress questions and responses so the draft survives a reload. */
  private saveDraft(): void {
    if (this._contentId === undefined || this._workflowId === undefined) return;
    LocalStorage.setContentQualityQuestionnaireState(
      this._contentId,
      this._workflowId,
      ContentQualityQuestionnaireState.createNew(
        this._questions,
        responsesMapToArray(this._responseMap),
      ),
    );
  }

  /** Remove the persisted draft (after a successful submit). */
  private clearDraft(): void {
    if (this._contentId === undefined || this._workflowId === undefined) return;
    LocalStorage.resetContentQualityQuestionnaireState(this._contentId, this._workflowId);
  }

  /** Returns an error message when the current state cannot be submitted, else undefined. */
  private validateForSubmit(
    requireValidScore: boolean,
    releasePriority?: ContentPriority,
  ): string | undefined {
    // Publish requires a valid score.
    if (requireValidScore && !isScoreValid(this._score)) {
      return 'Answer all required questions before approving.';
    }

    // External-audience approvals require a release priority.
    if (requireValidScore && this._requiresReleasePriority && releasePriority === undefined) {
      return 'Select a public release priority before approving.';
    }
    return undefined;
  }
}
